import { OkamiHttpGatewayError, isForbiddenError, isUnauthorizedError } from '@/api/errors.ts'
import { isRefreshTokenCall, refreshTokenCall } from '@/api/refresh-token.ts'
import { failRequestQueue } from '@/lib/axios/fail-request-queue.ts'
import { eventsManager } from '@/utils/events-manager.ts'
import axios, { AxiosRequestConfig } from 'axios'
import { toast } from 'sonner'

export const okamiHttpGateway = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true,
})

let isRefreshing = false

okamiHttpGateway.interceptors.request.use(
  (request) => {
    if (isRefreshing && !isRefreshTokenCall(request)) {
      console.log('Request is being refreshed, waiting for the token to be updated... ', request.url)

      return new Promise((resolve, reject) => {
        failRequestQueue.add(request.url!, resolve, reject)
      })
    }

    return request
  },
  (error) => error,
)

okamiHttpGateway.interceptors.response.use(
  (response) => response,
  async (error: OkamiHttpGatewayError) => {
    const originalRequest = error.config

    if (isUnauthorizedError(error)) {
      if (isRefreshing) {
        return Promise.reject(error)
      }

      isRefreshing = true

      try {
        await refreshTokenCall()

        isRefreshing = false
        eventsManager.dispatch('auth:authenticated', { time: Date.now() })

        failRequestQueue.resolveAll()

        return okamiHttpGateway(originalRequest as AxiosRequestConfig)
      } catch (e) {
        isRefreshing = false
        eventsManager.dispatch('auth:unauthenticated', { time: Date.now() })

        failRequestQueue.rejectAll(e as OkamiHttpGatewayError)
        return Promise.reject(e)
      } finally {
        failRequestQueue.clear()
      }
    }

    if (isForbiddenError(error)) {
      toast.error('Você não tem as permissões necessárias para acessar')
    }

    return Promise.reject(
      new OkamiHttpGatewayError(error.message, error.code, error.config, error.request, error.response),
    )
  },
)

export const customInstance = <T>(config: AxiosRequestConfig, options?: AxiosRequestConfig): Promise<T> => {
  const controller = new AbortController()

  const promise = okamiHttpGateway({
    ...config,
    ...options,
    signal: controller.signal,
  }).then(({ data }) => data)

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  promise.cancel = () => {
    controller.abort()
  }

  return promise
}
